import * as React from 'react'

/**
 * Tracks whether an element has entered (or come within `rootMargin` of) the viewport. Latches to
 * `true` on first intersection and then stops observing — used to lazily trigger work (e.g. fetching
 * a price) only for elements the user actually scrolls to.
 *
 * All hook instances sharing a `rootMargin` reuse a single IntersectionObserver, so a list of
 * hundreds of items costs one observer rather than one per item.
 *
 * Falls back to `true` where IntersectionObserver is unavailable (SSR, jsdom, old browsers) so the
 * gated work still runs.
 *
 * @example
 * const [ref, inView] = useInViewport<HTMLDivElement>()
 * return <div ref={ref}>{inView ? <Price /> : <Skeleton />}</div>
 */
interface SharedObserver {
  observer: IntersectionObserver
  callbacks: Map<Element, () => void>
}

const sharedObservers = new Map<string, SharedObserver>()

const getSharedObserver = (rootMargin: string): SharedObserver => {
  const existing = sharedObservers.get(rootMargin)
  if (existing) {
    return existing
  }

  const callbacks = new Map<Element, () => void>()
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callbacks.get(entry.target)?.()
        }
      })
    },
    { rootMargin }
  )

  const shared: SharedObserver = { observer, callbacks }
  sharedObservers.set(rootMargin, shared)
  return shared
}

const unobserve = (shared: SharedObserver, node: Element) => {
  shared.callbacks.delete(node)
  shared.observer.unobserve(node)
}

export const useInViewport = <T extends Element>(
  rootMargin = '300px'
): [ref: React.RefCallback<T>, inView: boolean] => {
  const supported = typeof IntersectionObserver !== 'undefined'
  const [inView, setInView] = React.useState(!supported)
  const inViewRef = React.useRef(inView)
  inViewRef.current = inView
  const nodeRef = React.useRef<T | null>(null)

  const setRef = React.useCallback(
    (node: T | null) => {
      if (!supported) {
        return
      }

      const shared = getSharedObserver(rootMargin)

      if (nodeRef.current) {
        unobserve(shared, nodeRef.current)
      }
      nodeRef.current = node

      if (node == null || inViewRef.current) {
        return
      }

      shared.callbacks.set(node, () => {
        setInView(true)
        unobserve(shared, node)
      })
      shared.observer.observe(node)
    },
    [rootMargin, supported]
  )

  React.useEffect(
    () => () => {
      if (supported && nodeRef.current) {
        unobserve(getSharedObserver(rootMargin), nodeRef.current)
      }
    },
    [rootMargin, supported]
  )

  return [setRef, inView]
}
